import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Download, MapPin, Phone, Mail, FileText, Award } from "lucide-react";
import { api, errMsg, downloadResume } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, BeltBadge, StatusPill } from "../../components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";

const STAGES = ["applied", "shortlisted", "interview", "offered", "hired", "rejected"];

const ScoreRing = ({ score }) => (
  <div className="relative w-14 h-14 shrink-0">
    <svg viewBox="0 0 36 36" className="w-14 h-14 -rotate-90">
      <circle cx="18" cy="18" r="15.5" fill="none" stroke="#F4EEF7" strokeWidth="3" />
      <circle cx="18" cy="18" r="15.5" fill="none" stroke={score >= 70 ? "#D4AF37" : "#5B2A6E"} strokeWidth="3" strokeDasharray={`${score * 0.974} 100`} strokeLinecap="round" />
    </svg>
    <span className="absolute inset-0 grid place-items-center font-display font-bold text-sm">{score}%</span>
  </div>
);

const MatchCard = ({ m, onShortlist, canAct }) => (
  <div className="dx-card p-5" data-testid={`match-${m.staff_id}`}>
    <div className="flex gap-4">
      <ScoreRing score={m.score} />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2"><span className="font-display font-semibold">{m.name}</span><BeltBadge belt={m.highest_belt} /></div>
        <div className="text-sm text-[#6b5a73]">{m.headline || "No headline"} · {m.experience_years} yrs · {m.location || "-"}</div>
        <div className="flex flex-wrap gap-1 mt-2">{m.skills.map((s) => <span key={s} className={`text-xs rounded-full px-2 py-0.5 ${m.matched_skills.includes(s.toLowerCase()) ? "bg-[#D4AF37] text-[#250E31] font-semibold" : "bg-[#F4EEF7] text-[#5B2A6E]"}`}>{s}</span>)}</div>
        {(m.resume_hits?.length > 0 || m.resume_keywords?.length > 0) && (
          <div className="flex flex-wrap items-center gap-1 mt-2" data-testid={`match-${m.staff_id}-resume-hits`}>
            <FileText className="w-3.5 h-3.5 text-[#5B2A6E]" /><span className="text-[11px] text-[#6b5a73]">Found in resume:</span>
            {[...m.resume_hits, ...m.resume_keywords].map((s) => <span key={s} className="text-xs rounded-full px-2 py-0.5 border border-[#D4AF37] text-[#5B2A6E]">{s}</span>)}
          </div>
        )}
        {m.certifications?.length > 0 && <div className="text-[11px] text-[#5B2A6E] mt-2 flex items-center gap-1"><Award className="w-3.5 h-3.5 text-[#D4AF37]" />{m.certifications.join(" · ")}</div>}
        <div className="text-[11px] text-[#8a7a92] mt-2">Skills {m.breakdown.skills}/40 · Belt {m.breakdown.certification}/25 · Resume {m.breakdown.resume}/15 · Location {m.breakdown.location}/10 · Experience {m.breakdown.experience}/10</div>
      </div>
    </div>
    <div className="flex flex-wrap gap-2 mt-4 justify-end">
      {m.has_resume && <button className="dx-btn-ghost" onClick={() => downloadResume(m.staff_id, `${m.name}-resume`)} data-testid={`match-${m.staff_id}-resume-btn`}><Download className="w-4 h-4" />Resume</button>}
      {m.application_status ? <StatusPill status={m.application_status} testid={`match-${m.staff_id}-status`} />
        : canAct && <button className="dx-btn-gold" onClick={() => onShortlist(m.staff_id)} data-testid={`match-${m.staff_id}-shortlist-btn`}>Shortlist</button>}
    </div>
  </div>
);

const Pipeline = ({ apps, onMove, canAct }) => (
  <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
    {STAGES.map((s) => (
      <div key={s} className="rounded-2xl bg-[#FAF8FC] border p-3" data-testid={`pipeline-${s}`}>
        <div className="flex justify-between items-center mb-2 px-1"><StatusPill status={s} /><span className="text-xs text-[#8a7a92]">{apps.filter((a) => a.status === s).length}</span></div>
        <div className="space-y-2">
          {apps.filter((a) => a.status === s).map((a) => (
            <div key={a.id} className="bg-white rounded-xl border p-3 text-sm" data-testid={`application-${a.id}`}>
              <div className="flex justify-between gap-2"><span className="font-semibold">{a.staff_name}</span><span className="font-display font-bold text-[#5B2A6E]">{a.match_score}%</span></div>
              <div className="text-xs text-[#6b5a73] mt-1 space-y-0.5">
                {a.location && <div className="flex items-center gap-1"><MapPin className="w-3 h-3" />{a.location}</div>}
                {a.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{a.phone}</div>}
                {a.email && <div className="flex items-center gap-1 break-all"><Mail className="w-3 h-3" />{a.email}</div>}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <BeltBadge belt={a.highest_belt} />
                {a.has_resume && <button onClick={() => downloadResume(a.staff_id, `${a.staff_name}-resume`)} className="text-xs text-[#5B2A6E] underline" data-testid={`application-${a.id}-resume`}>Resume</button>}
              </div>
              {canAct && (
                <select className="dx-input mt-2 py-1.5 text-xs" value={a.status} onChange={(e) => onMove(a.id, e.target.value)} data-testid={`application-${a.id}-status-select`}>
                  {STAGES.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              )}
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [matches, setMatches] = useState([]);
  const [apps, setApps] = useState([]);
  const canAct = user.role === "employer";

  const load = () => Promise.all([api.get(`/jobs/${id}`), api.get(`/jobs/${id}/matches`), api.get(`/jobs/${id}/applications`)])
    .then(([j, m, a]) => { setJob(j.data); setMatches(m.data); setApps(a.data); });
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!job) return null;

  return (
    <div>
      <PageHeader eyebrow={`${job.company_name} · ${job.location} · ${job.job_type}`} title={job.title} subtitle={job.description}
        action={canAct && <button className="dx-btn-ghost" onClick={() => act(() => api.patch(`/jobs/${id}/status`, { status: job.status === "open" ? "closed" : "open" }), "Job updated")} data-testid="job-toggle-status-btn">{job.status === "open" ? "Close job" : "Reopen job"}</button>} />
      <div className="flex flex-wrap gap-2 mb-6">
        <StatusPill status={job.status} testid="job-status" />
        {job.min_belt !== "none" && <BeltBadge belt={job.min_belt} />}
        {job.skills.map((s) => <span key={s} className="text-xs rounded-full bg-[#F4EEF7] text-[#5B2A6E] px-2.5 py-1">{s}</span>)}
      </div>
      <Tabs defaultValue="matches">
        <TabsList className="mb-5">
          <TabsTrigger value="matches" data-testid="tab-matches"><Sparkles className="w-4 h-4 mr-1.5 text-[#D4AF37]" />Automatch ({matches.length})</TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="tab-pipeline">Pipeline ({apps.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="matches">
          <p className="text-sm text-[#6b5a73] mb-4">Candidates ranked by skills (from their profile and resume), Lean Six Sigma belt, resume content, location and experience.</p>
          <div className="grid lg:grid-cols-2 gap-4">
            {matches.map((m) => <MatchCard key={m.staff_id} m={m} canAct={canAct} onShortlist={(sid) => act(() => api.post(`/jobs/${id}/shortlist/${sid}`), "Candidate shortlisted")} />)}
          </div>
        </TabsContent>
        <TabsContent value="pipeline">
          <Pipeline apps={apps} canAct={canAct} onMove={(aid, status) => act(() => api.patch(`/applications/${aid}`, { status }), status === "hired" ? "Hired - added to your team" : "Status updated")} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
